import { Injectable, OnDestroy } from '@angular/core';
import * as GraphClient from '@microsoft/microsoft-graph-client';
import { User as GraphUser } from '@microsoft/microsoft-graph-types';
import { select, Store } from '@ngrx/store';
import { LoggingService } from '@services/logging.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { selectEnvironmentAppSetting, selectStudyAccessAllowedAddressesAppSetting } from '../app.selectors';
import { AppState } from '../reducers';
import { AuthService } from './auth.service';

@Injectable()
export class GraphService implements OnDestroy {
  private destroy$ = new Subject();

  private environment: string;
  private allowedAddresses: string[];

  public currentUser: GraphUser;

  constructor(
    private authService: AuthService,
    private logger: LoggingService,
    private store: Store<AppState>,
  ) {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectEnvironmentAppSetting))
      .subscribe((env) => (this.environment = env));

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectStudyAccessAllowedAddressesAppSetting))
      .subscribe((addresses) => (this.allowedAddresses = addresses === null ? [] : addresses.split(';')));
  }

  ngOnDestroy(): void {
    this.destroy$.next('');
  }

  public async getCurrentUser(): Promise<GraphUser> {
    this.logger.info(this.constructor.name, 'getCurrentUser', 'Start');
    try {
      if (!this.currentUser) {
        this.currentUser = await this.getSpecificUser('me');
      }

      return this.currentUser;
    } finally {
      this.logger.info(this.constructor.name, 'getCurrentUser', 'End');
    }
  }

  public async getSpecificUser(userId: string): Promise<GraphUser> {
    this.logger.info(this.constructor.name, `getSpecificUser:[${userId}]`, 'Start');
    let whereAmI = '';
    let specificUser: GraphUser;

    const access_token = await this.authService.getAzureADAccessToken();
    if (access_token && access_token.length > 0) {
      const client = GraphClient.Client.init({
        authProvider: (done) => {
          done(null, access_token);
        },
      });

      // refactored to pass 'ng lint' line length test
      const extension = 'extension_2cc2f842b72044dcaf51a3fc59fc27c8';
      const select = `businessPhones,mail,mobilePhone,preferredLanguage,id,displayname,
          jobTitle,officeLocation,UserPrincipalName,Surname,GivenName,Id,city,country,
          ${extension}_EYBusinessUnitCode,${extension}_EYLocationAddressCountry,${extension}_EYOperatingUnitCode,
          ${extension}_EYOperatingUnitDesc,${extension}_EYServiceLineCode,${extension}_EYServiceLineDesc,
          ${extension}_EYSubServiceLineCode,${extension}_EYSubServiceLineDesc,${extension}_EYSubManagementUnitCode,
          ${extension}_EYSubManagementUnitDesc,${extension}_EYSubAreaCode1,${extension}_EYSubAreaCode2`;

      const url: string = userId == 'me' ? '/me' : `/users/${userId}`;
      specificUser = <GraphUser>await (<GraphUser>client
        .api(`/${url}?$select=${select}`)
        .get()
        .catch((e) => {
          //As part of BUG 935214 fix removed resource not found error logging.
        }));
      delete specificUser['@odata.context'];
      whereAmI = 'From Graph API';
    } else {
      whereAmI = 'Unable to get Azure AD Access Token';
    }

    this.logger.info(this.constructor.name, 'getSpecificUser', `[${whereAmI}] ${JSON.stringify(specificUser)}`);
    return specificUser;
  }

  public async findUsers(filterText: string, returnCount = 100): Promise<GraphUser[]> {
    this.logger.info(this.constructor.name, 'findUsers', `Start filterText=[${filterText}]`);

    const access_token = await this.authService.getAzureADAccessToken();
    if (access_token && access_token.length > 0) {
      const client = GraphClient.Client.init({
        authProvider: (done) => {
          done(null, access_token);
        },
      });

      const myFilter = `startswith(displayName,'${filterText}') or startswith(givenName,'${filterText}') or startswith(surname,'${filterText}') or startswith(mail,'${filterText}') or startswith(userPrincipalName,'${filterText}')`;
      this.logger.info(this.constructor.name, 'findUsers', `myFilter=[${myFilter}]`);

      const result = <any>await (<any>client
        .api('/users')
        .filter(myFilter)
        .top(returnCount)
        .get()
        .catch((e) => this.logger.httpError(this.constructor.name, 'findUsers', e)));

      let users: GraphUser[] = [];

      if (result && result.value) {
        if (this.environment === 'DEV' || this.environment === 'DEV0'|| this.environment === 'QA') {
          users = result.value;
        }
        else {
          users = result.value.filter((a) => a.mail !== null && this.allowedAddresses.some((address) => a.mail.endsWith(address)));
        }
      }
      return users;
    }
    return [];
  }

  public getEnvironment(): string {
    return this.environment;
  }
}
